import axios from "axios";
import { plainToClass } from "class-transformer";
import TabularDatasetRow from "@/models/TabularDatasetRow.ts";

export default class TabularDataset {
  // WTF TypeScript???
  // load the dataset from the database
  static async load(id: number): Promise<TabularDataset> {
    const url = [
      `/psql/tabular_datasets?id=eq.${id}&select=*`,
      "dataset:tabular_datasets_records(*)",
      "geo_entity:geo_entities(name)",
      "document:documents(name)",
      "source:publishers(name)",
    ].join(",");
    let response = await axios.get(url, {
      headers: {
        Accept: "application/vnd.pgrst.object+json",
      },
    });
    let data: object = response.data;
    let tds = plainToClass(TabularDataset, data);
    tds.dataset = plainToClass(TabularDatasetRow, tds.dataset);
    return tds;
  }

  // Meta data
  // -------------------------------------------------------------------
  id: number | null = null;
  name: string = "";
  iso3: string | null = null;
  displacement_type: string | null = null;
  source_id: number | null = null;
  document_id: number | null = null;
  unit: string | null = null;
  url: string | null = null;
  age_class: string | null = null;
  comments: string | null = null;
  created_by: string | null = null;
  created_at: Date | null = null;
  locked_at: Date | null = null;

  // Aux data
  // -------------------------------------------------------------------
  geo_entity?: any;
  document?: any;
  source?: any;

  // Dataset
  // -------------------------------------------------------------------
  dataset: TabularDatasetRow[] = [];

  // methods
  // -------------------------------------------------------------------
  isLocked() {
    return !!this.locked_at;
  }

  concat(data: TabularDatasetRow[]) {
    let rows: TabularDatasetRow[] = plainToClass(TabularDatasetRow, data);
    this.removeEmptyRows();
    for (const row of rows) {
      row.iso3 = this.iso3;
      row.parent_id = this.id;
    }
    this.dataset = this.dataset.concat(rows);
  }

  clear() {
    this.dataset = [];
  }

  removeDeleted() {
    this.dataset = this.dataset.filter(row => {
      return !(row.isNew() && row.isDeleted());
    });
  }

  addEmptyRows(n: number) {
    const empty: any = new Array(n).fill({
      iso3: this.iso3,
      parent_id: this.id,
    });
    let rows: TabularDatasetRow[] = plainToClass(TabularDatasetRow, empty);
    this.dataset = this.dataset.concat(rows);
  }

  removeEmptyRows() {
    this.dataset = this.dataset.filter(
      (row: TabularDatasetRow) => !row.isEmpty()
    );
  }

  isColumnEmpty(field: string) {
    return this.dataset.reduce(
      (visible: boolean, row: any) => visible || !!row[field],
      false
    );
  }

  debug() {
    console.debug(this);
  }

  // Database access
  // -------------------------------------------------------------------

  // create dataset with metadata only
  async create() {
    const tabularDataset = { ...this };
    delete tabularDataset.dataset;
    delete tabularDataset.id;
    return axios.post("/psql/tabular_datasets", tabularDataset, {
      headers: {
        Accept: "application/vnd.pgrst.object+json",
        Prefer: "return=representation",
      },
    });
  }

  // Remove fields from the row to save in the database
  dbRow(row: TabularDatasetRow) {
    const rowClone = { ...row };
    if (!rowClone.id) delete rowClone.id;
    // @ts-ignore
    delete rowClone._deleted;
    // @ts-ignore
    delete rowClone._updated;
    return rowClone;
  }

  // Save the dataset into the database
  save(_lock: boolean = false) {
    if (this.isLocked()) {
      throw new Error("Dataset is locked!");
    }
    // TODO Validate rows
    this.removeEmptyRows();

    // Save
    return axios.post(
      "/psql/rpc/save_tabular_dataset_records",
      { api_data: { ...this, _lock } },
      {
        headers: {
          Accept: "application/vnd.pgrst.object+json",
        },
      }
    );
  }

  async updateComments() {
    return axios.patch(
      "/psql/tabular_datasets",
      { comments: this.comments },
      {
        params: {
          id: `eq.${this.id}`,
        },
      }
    );
  }
}
